import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { queuePendingOrder } from "@/lib/offlineOrders";
import { formatFCFA } from "@/lib/format";
import { SelectField, TextField, TextArea } from "@/components/Modal";
import { Loader } from "@/components/common";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedAddr, setSelectedAddr] = useState("new");
  const [pay, setPay] = useState("livraison");
  const [wallet, setWallet] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const placed = useRef(false);
  const [f, setF] = useState({ label: "Domicile", zone: "", quartier: "", address: "", phone: "", notes: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const [z, a] = await Promise.all([api.get("/zones"), api.get("/addresses")]);
        setZones(z.data); setAddresses(a.data);
        const def = a.data.find((x) => x.is_default) || a.data[0];
        if (def) { setSelectedAddr(def.id); }
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { if (items.length === 0 && !placed.current) navigate("/panier"); }, [items, navigate]);

  useEffect(() => { api.get("/client-referral").then((r) => setWallet(r.data)).catch(() => {}); }, []);

  const zone = zones.find((z) => z.name === (selectedAddr === "new" ? f.zone : addresses.find((a) => a.id === selectedAddr)?.zone));
  const deliveryFee = zone ? zone.delivery_fee : 0;
  const refereeDiscount = wallet?.referee_eligible ? Math.min(wallet.referee_reward, subtotal) : 0;
  const preWallet = subtotal + deliveryFee - refereeDiscount;
  const walletAvail = wallet?.wallet_balance || 0;
  const walletUse = useWallet ? Math.min(walletAvail, preWallet) : 0;
  const total = Math.max(0, preWallet - walletUse);

  const submit = async (e) => {
    e.preventDefault();
    let address, zoneName;
    if (selectedAddr === "new") {
      if (!f.zone || !f.address) return toast.error("Renseignez la zone et l'adresse");
      address = { label: f.label, zone: f.zone, quartier: f.quartier, address: f.address, phone: f.phone };
      zoneName = f.zone;
    } else {
      const a = addresses.find((x) => x.id === selectedAddr);
      address = a; zoneName = a.zone;
    }
    setBusy(true);
    const payload = { items, address, zone: zoneName, notes: f.notes, payment_method: pay, wallet_use: walletUse };
    if (!navigator.onLine) {
      queuePendingOrder(payload);
      placed.current = true; clear();
      toast.success("Hors-ligne : commande enregistrée, elle sera envoyée automatiquement dès le retour du réseau.");
      navigate("/commandes");
      setBusy(false);
      return;
    }
    try {
      const { data } = await api.post("/orders", payload);
      placed.current = true;
      clear();
      toast.success("Commande passée avec succès !");
      navigate(`/commandes/${data.id}`);
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || !err.response) {
        queuePendingOrder(payload);
        placed.current = true; clear();
        toast.success("Réseau indisponible : commande mise en file, envoi automatique au retour du réseau.");
        navigate("/commandes");
      } else {
        toast.error(apiError(err));
      }
    } finally { setBusy(false); }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-[#0F172A] mb-5">Finaliser la commande</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h2 className="font-display text-xl font-bold">Adresse de livraison</h2>
            {addresses.length > 0 && (
              <div className="space-y-2">
                {addresses.map((a) => (
                  <label key={a.id} className={`flex gap-3 items-start p-3 rounded-lg border-2 cursor-pointer ${selectedAddr === a.id ? "border-[#C85A32] bg-[#FDF3EE]" : "border-slate-200"}`} data-testid={`addr-option-${a.id}`}>
                    <input type="radio" checked={selectedAddr === a.id} onChange={() => setSelectedAddr(a.id)} className="mt-1" />
                    <div><p className="text-sm font-semibold">{a.label} — {a.zone}</p><p className="text-xs text-slate-500">{a.address} · {a.phone}</p></div>
                  </label>
                ))}
                <label className={`flex gap-3 items-center p-3 rounded-lg border-2 cursor-pointer ${selectedAddr === "new" ? "border-[#C85A32] bg-[#FDF3EE]" : "border-slate-200"}`} data-testid="addr-option-new">
                  <input type="radio" checked={selectedAddr === "new"} onChange={() => setSelectedAddr("new")} />
                  <span className="text-sm font-semibold">Nouvelle adresse</span>
                </label>
              </div>
            )}
            {selectedAddr === "new" && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <TextField label="Libellé" value={f.label} onChange={set("label")} testid="co-label" />
                  <SelectField label="Zone de livraison" value={f.zone} onChange={set("zone")} testid="co-zone" options={zones.map((z) => ({ value: z.name, label: `${z.name} (${formatFCFA(z.delivery_fee)})` }))} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <TextField label="Quartier / secteur" value={f.quartier} onChange={set("quartier")} testid="co-quartier" required={false} />
                  <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="co-phone" required={false} placeholder="+228 ..." />
                </div>
                <TextField label="Adresse complète" value={f.address} onChange={set("address")} testid="co-address" />
              </div>
            )}
            <TextArea label="Notes de livraison (facultatif)" value={f.notes} onChange={set("notes")} testid="co-notes" required={false} rows={2} />
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="payment-methods">
            <h2 className="font-display text-xl font-bold">Mode de paiement</h2>
            <label className={`flex gap-3 items-start p-3 rounded-lg border-2 cursor-pointer ${pay === "livraison" ? "border-[#C85A32] bg-[#FDF3EE]" : "border-slate-200"}`} data-testid="pay-livraison">
              <input type="radio" checked={pay === "livraison"} onChange={() => setPay("livraison")} className="mt-1" />
              <div><p className="text-sm font-semibold">Paiement à la livraison</p><p className="text-xs text-slate-500">Mobile Money ou espèces à la réception / au bureau.</p></div>
            </label>
            <label className="flex gap-3 items-start p-3 rounded-lg border-2 border-slate-200 opacity-60 cursor-not-allowed" data-testid="pay-enligne">
              <input type="radio" disabled className="mt-1" />
              <div><p className="text-sm font-semibold">Paiement en ligne <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">Bientôt</span></p><p className="text-xs text-slate-500">Carte bancaire & Mobile Money en ligne — disponible prochainement.</p></div>
            </label>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
            💡 Le paiement s'effectue à la livraison ou au bureau (Mobile Money / espèces). Le paiement en ligne CinetPay sera bientôt disponible.
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit sticky top-24">
          <h2 className="font-display text-xl font-bold mb-3">Votre commande</h2>
          <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar mb-3">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-slate-600 line-clamp-1">{i.quantity}× {i.name}</span>
                <span className="font-num font-semibold shrink-0 ml-2">{formatFCFA(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Sous-total</span><span className="font-num">{formatFCFA(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Livraison</span><span className="font-num">{deliveryFee ? formatFCFA(deliveryFee) : "—"}</span></div>
            {refereeDiscount > 0 && (
              <div className="flex justify-between text-green-700" data-testid="referee-discount-row"><span>Réduction parrainage</span><span className="font-num">−{formatFCFA(refereeDiscount)}</span></div>
            )}
            {walletUse > 0 && (
              <div className="flex justify-between text-green-700"><span>Crédit utilisé</span><span className="font-num">−{formatFCFA(walletUse)}</span></div>
            )}
            <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span className="font-num text-[#C85A32]" data-testid="checkout-total">{formatFCFA(total)}</span></div>
          </div>
          {walletAvail > 0 && (
            <label className="flex items-center gap-2 mt-3 text-sm bg-amber-50 border border-amber-200 rounded-lg p-2.5 cursor-pointer" data-testid="use-wallet-toggle">
              <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} data-testid="use-wallet-checkbox" />
              <span>Utiliser mon crédit <strong>({formatFCFA(walletAvail)})</strong></span>
            </label>
          )}
          <button disabled={busy} className="mt-4 w-full bg-[#C85A32] hover:bg-[#A94520] text-white font-bold py-3 rounded-lg disabled:opacity-50 transition active:scale-95" data-testid="place-order-btn">
            {busy ? "Traitement..." : "Confirmer la commande"}
          </button>
        </div>
      </form>
    </div>
  );
}
